import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollection } from '@/hooks/useFirestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { where } from 'firebase/firestore';
import { 
  Monitor, 
  MapPin, 
  Users, 
  Image as ImageIcon,
  CheckCircle2,
  XCircle,
  Activity,
  TrendingUp
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const StatCard = ({ title, value, icon: Icon, description, trend, color = 'indigo' }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center mt-4 text-xs text-green-400">
          <TrendingUp className="w-3 h-3 mr-1" />
          {trend}
        </div>
      )}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { userProfile, isHQ } = useAuth();
  
  // Fetch data based on user role
  const locationsQuery = isHQ ? [] : [where('id', 'in', userProfile?.assignedLocations || ['none'])];
  const { documents: locations, loading: locationsLoading } = useCollection('locations', locationsQuery);
  const { documents: screens } = useCollection('screens');
  const { documents: users } = useCollection('users');
  const { documents: media } = useCollection('media');

  const activeLocations = locations.filter(l => l.status === 'active');
  const onlineScreens = screens.filter(s => s.status === 'online');
  const onlineUsers = users.filter(u => u.isOnline);

  // Chart data
  const screensByLocation = locations.map(loc => ({
    name: loc.name?.substring(0, 10) || 'Unknown',
    screens: screens.filter(s => s.locationId === loc.id).length
  }));

  const screenStatusData = [
    { name: 'Online', value: onlineScreens.length, color: '#22c55e' },
    { name: 'Offline', value: screens.length - onlineScreens.length, color: '#ef4444' }
  ];

  if (locationsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400">Welcome back, {userProfile?.name || userProfile?.email}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Locations" 
          value={locations.length}
          icon={MapPin}
          description={`${activeLocations.length} active`}
          color="indigo"
        />
        <StatCard 
          title="Total Screens" 
          value={screens.length}
          icon={Monitor}
          description={`${onlineScreens.length} online`}
          color="purple"
        />
        <StatCard 
          title="Active Users" 
          value={onlineUsers.length}
          icon={Users}
          description={`of ${users.length} total`}
          color="green"
        />
        <StatCard 
          title="Media Files" 
          value={media.length}
          icon={ImageIcon}
          description="Images & Videos"
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Screens by Location</CardTitle>
            <CardDescription>Distribution of screens across locations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={screensByLocation}>
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                    labelStyle={{ color: '#f8fafc' }}
                  />
                  <Bar dataKey="screens" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Screen Status</CardTitle>
            <CardDescription>Online vs Offline screens</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={screenStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {screenStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-slate-400">Online ({onlineScreens.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm text-slate-400">Offline ({screens.length - onlineScreens.length})</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Locations List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Locations Overview</CardTitle>
          <CardDescription>All locations with screen counts</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-72">
            <div className="space-y-2">
              {locations.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No locations found. Add your first location!</p>
              ) : (
                locations
                  .sort((a, b) => (a.city || '').localeCompare(b.city || '') || (a.name || '').localeCompare(b.name || ''))
                  .map((location) => {
                    const locationScreens = screens.filter(s => s.locationId === location.id);
                    const onlineCount = locationScreens.filter(s => s.status === 'online').length;
                    return (
                      <div 
                        key={location.id} 
                        className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{location.name}</p>
                            <p className="text-sm text-slate-400">{location.city}, {location.address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-slate-300">{locationScreens.length} screens</p>
                            <div className="flex items-center gap-1 text-xs">
                              <CheckCircle2 className="w-3 h-3 text-green-400" />
                              <span className="text-green-400">{onlineCount}</span>
                              <XCircle className="w-3 h-3 text-red-400 ml-2" />
                              <span className="text-red-400">{locationScreens.length - onlineCount}</span>
                            </div>
                          </div>
                          <Badge 
                            variant={location.status === 'active' ? 'default' : 'secondary'}
                            className={location.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600'}
                          >
                            {location.status || 'active'}
                          </Badge>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
